import { useNavigate } from 'react-router-dom';
import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../i18n';
import { toast } from 'sonner';
import {
  Files, Sparkles, Trash2, Loader2, Download, Eye, Maximize2, Volume2, Upload,
  Search, Share2, FileText, Image, FileSpreadsheet, File, ChevronUp,
  ChevronDown, MoreHorizontal, Send, CheckCircle2, Clock, X, Copy,
  Link, Mail, ArrowUpDown, History, Tag, Activity, Plus, RefreshCw,
  FolderOpen, FolderPlus, ChevronRight, RotateCcw, Archive, Pencil,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { FileUploader } from '../components/documents/FileUploader';

// --- Helpers ---
const FILE_ICONS = {
  'application/pdf': { icon: FileText, color: 'text-red-500' },
  'image/png': { icon: Image, color: 'text-blue-500' },
  'image/jpeg': { icon: Image, color: 'text-blue-500' },
  'image/jpg': { icon: Image, color: 'text-blue-500' },
  'text/plain': { icon: FileSpreadsheet, color: 'text-green-500' },
  'text/csv': { icon: FileSpreadsheet, color: 'text-green-500' },
  'application/msword': { icon: FileText, color: 'text-blue-600' },
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': { icon: FileText, color: 'text-blue-600' },
};

function getFileIcon(mimeType) {
  return FILE_ICONS[mimeType] || { icon: File, color: 'text-muted-foreground' };
}

function formatBytes(bytes) {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function timeAgo(dateStr) {
  if (!dateStr) return '—';
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now - date;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDays = Math.floor(diffHr / 24);
  if (diffDays < 30) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

const STATUS_MAP = {
  draft: { label: 'Draft', color: 'bg-gray-500/10 text-gray-600 border-gray-200 dark:text-gray-400' },
  uploaded: { label: 'Uploaded', color: 'bg-amber-500/10 text-amber-600 border-amber-200 dark:text-amber-400' },
  processed: { label: 'Analyzed', color: 'bg-green-500/10 text-green-600 border-green-200 dark:text-green-400' },
  sent: { label: 'Sent', color: 'bg-blue-500/10 text-blue-600 border-blue-200 dark:text-blue-400' },
  viewed: { label: 'Viewed', color: 'bg-purple-500/10 text-purple-600 border-purple-200 dark:text-purple-400' },
  signed: { label: 'Signed', color: 'bg-emerald-500/10 text-emerald-600 border-emerald-200 dark:text-emerald-400' },
  archived: { label: 'Archived', color: 'bg-gray-500/10 text-gray-500 border-gray-200 dark:text-gray-400' },
  error: { label: 'Error', color: 'bg-red-500/10 text-red-600 border-red-200 dark:text-red-400' },
};

const DOC_TYPES = [
  { value: 'all', label: 'All Types' },
  { value: 'contract', label: 'Contract' },
  { value: 'invoice', label: 'Invoice' },
  { value: 'license', label: 'License' },
  { value: 'certificate', label: 'Certificate' },
  { value: 'registration', label: 'Registration' },
  { value: 'other', label: 'Other' },
];

// ==================== MAIN COMPONENT ====================
export default function DocumentsPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();

  // --- State ---
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedDocs, setSelectedDocs] = useState([]);
  const [drawerDoc, setDrawerDoc] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareDoc, setShareDoc] = useState(null);
  const [requestOpen, setRequestOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [docToDelete, setDocToDelete] = useState(null);
  const [ttsPlaying, setTtsPlaying] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Share form
  const [shareEmail, setShareEmail] = useState('');
  const [shareMessage, setShareMessage] = useState('');
  const [shareResult, setShareResult] = useState(null);

  // Request form
  const [requestEmail, setRequestEmail] = useState('');
  const [requestName, setRequestName] = useState('');
  const [requestMessage, setRequestMessage] = useState('');
  const [requestFiles, setRequestFiles] = useState(['']);

  // Drawer tabs
  const [drawerTab, setDrawerTab] = useState('details');
  const [tagInput, setTagInput] = useState('');
  const [versionUploadOpen, setVersionUploadOpen] = useState(false);

  // Folders
  const [currentFolderId, setCurrentFolderId] = useState(null); // null = all, 'root' = root only
  const [folderPath, setFolderPath] = useState([]); // breadcrumb: [{id, name}, ...]
  const [createFolderOpen, setCreateFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [renameFolderOpen, setRenameFolderOpen] = useState(null); // folder object
  const [renameFolderName, setRenameFolderName] = useState('');
  const [viewMode, setViewMode] = useState('all'); // 'all' | 'archived'
  const [moveDocsOpen, setMoveDocsOpen] = useState(false);

  // --- Queries ---
  const { data: documents = [], isLoading } = useQuery({
    queryKey: ['documents', statusFilter, typeFilter, searchQuery, sortBy, sortOrder, currentFolderId, viewMode],
    queryFn: () => {
      const params = new URLSearchParams();
      if (viewMode === 'archived') {
        params.set('status', 'archived');
      } else {
        if (statusFilter !== 'all') params.set('status', statusFilter);
      }
      if (typeFilter !== 'all') params.set('doc_type', typeFilter);
      if (searchQuery.trim()) params.set('search', searchQuery.trim());
      if (currentFolderId) params.set('folder_id', currentFolderId);
      params.set('sort_by', sortBy);
      params.set('sort_order', sortOrder);
      const qs = params.toString();
      return api.get(`/documents${qs ? `?${qs}` : ''}`);
    },
  });

  const { data: teamMembers = [] } = useQuery({
    queryKey: ['team'],
    queryFn: () => api.get('/team'),
  });

  // Drawer-specific queries (only fetch when drawer is open)
  const { data: versions = [] } = useQuery({
    queryKey: ['doc-versions', drawerDoc?._id],
    queryFn: () => api.get(`/documents/${drawerDoc._id}/versions`),
    enabled: !!drawerDoc && drawerTab === 'versions',
  });

  const { data: activities = [] } = useQuery({
    queryKey: ['doc-activity', drawerDoc?._id],
    queryFn: () => api.get(`/documents/${drawerDoc._id}/activity`),
    enabled: !!drawerDoc && drawerTab === 'activity',
  });

  const { data: orgTags = [] } = useQuery({
    queryKey: ['doc-tags'],
    queryFn: () => api.get('/documents/tags/list'),
  });

  // Folder queries
  const { data: folders = [] } = useQuery({
    queryKey: ['doc-folders', currentFolderId],
    queryFn: () => {
      const params = currentFolderId && currentFolderId !== 'root' ? `?parent_id=${currentFolderId}` : '';
      return api.get(`/documents/folders/list${params}`);
    },
  });

  // --- Mutations ---
  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/documents/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries(['documents']);
      toast.success('Document deleted');
      setDeleteDialogOpen(false);
      if (drawerDoc && drawerDoc._id === docToDelete) setDrawerDoc(null);
      setDocToDelete(null);
    },
    onError: () => toast.error('Failed to delete document'),
  });

  const analyzeMutation = useMutation({
    mutationFn: (id) => api.post(`/documents/${id}/analyze`),
    onSuccess: (data, id) => {
      queryClient.invalidateQueries(['documents']);
      toast.success('Document analyzed!');
      if (drawerDoc && drawerDoc._id === id) {
        setDrawerDoc(prev => ({ ...prev, status: 'processed', ai_extracted_data: data.analysis }));
      }
    },
    onError: () => toast.error('Analysis failed — check API key'),
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: async (ids) => {
      await Promise.all(ids.map(id => api.delete(`/documents/${id}`)));
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['documents']);
      toast.success(`${selectedDocs.length} documents deleted`);
      setSelectedDocs([]);
    },
  });

  const shareMutation = useMutation({
    mutationFn: ({ docId, email, message }) =>
      api.post(`/documents/${docId}/share`, { email: email || null, message: message || null }),
    onSuccess: (data) => {
      setShareResult(data);
      toast.success(shareEmail ? 'Share link sent!' : 'Share link created!');
    },
    onError: () => toast.error('Failed to create share link'),
  });

  const tagsMutation = useMutation({
    mutationFn: ({ docId, tags }) => api.patch(`/documents/${docId}/tags`, { tags }),
    onSuccess: (data, { docId }) => {
      queryClient.invalidateQueries(['documents']);
      if (drawerDoc && drawerDoc._id === docId) {
        setDrawerDoc(prev => ({ ...prev, tags: data.tags }));
      }
      toast.success('Tags updated');
    },
  });

  const statusMutation = useMutation({
    mutationFn: ({ docId, status }) => api.patch(`/documents/${docId}/status`, { status }),
    onSuccess: (data, { docId }) => {
      queryClient.invalidateQueries(['documents']);
      queryClient.invalidateQueries(['doc-activity', docId]);
      if (drawerDoc && drawerDoc._id === docId) {
        setDrawerDoc(prev => ({ ...prev, status: data.status }));
      }
      toast.success(`Status changed to ${data.status}`);
    },
  });

  const versionMutation = useMutation({
    mutationFn: async ({ docId, file }) => {
      // Get presigned URL
      const { presignedUrl, fileKey } = await api.post('/documents/presigned-url', {
        fileName: file.name,
        contentType: file.type || 'application/octet-stream',
      });
      // Upload to S3
      await fetch(presignedUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type || 'application/octet-stream',
          'x-amz-server-side-encryption': 'AES256',
        },
      });
      // Register version
      return api.post(`/documents/${docId}/version`, {
        fileKey,
        original_filename: file.name,
        mime_type: file.type || 'application/octet-stream',
        size: file.size,
      });
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(['documents']);
      queryClient.invalidateQueries(['doc-versions']);
      queryClient.invalidateQueries(['doc-activity']);
      toast.success(`Version ${data.version} uploaded`);
      setVersionUploadOpen(false);
    },
    onError: () => toast.error('Version upload failed'),
  });

  const createFolderMutation = useMutation({
    mutationFn: (body) => api.post('/documents/folders', body),
    onSuccess: () => {
      queryClient.invalidateQueries(['doc-folders']);
      toast.success('Folder created');
      setCreateFolderOpen(false);
      setNewFolderName('');
    },
    onError: (err) => toast.error(err.message || 'Failed to create folder'),
  });

  const renameFolderMutation = useMutation({
    mutationFn: ({ folderId, name }) => api.patch(`/documents/folders/${folderId}`, { name }),
    onSuccess: () => {
      queryClient.invalidateQueries(['doc-folders']);
      toast.success('Folder renamed');
      setRenameFolderOpen(null);
    },
  });

  const deleteFolderMutation = useMutation({
    mutationFn: (folderId) => api.delete(`/documents/folders/${folderId}`),
    onSuccess: () => {
      queryClient.invalidateQueries(['doc-folders']);
      queryClient.invalidateQueries(['documents']);
      toast.success('Folder deleted — documents moved to root');
    },
  });

  const moveDocsMutation = useMutation({
    mutationFn: (body) => api.post('/documents/folders/move-documents', body),
    onSuccess: (data) => {
      queryClient.invalidateQueries(['documents']);
      queryClient.invalidateQueries(['doc-folders']);
      toast.success(`${data.moved} document(s) moved`);
      setSelectedDocs([]);
      setMoveDocsOpen(false);
    },
  });

  const archiveMutation = useMutation({
    mutationFn: (id) => api.post(`/documents/${id}/archive`),
    onSuccess: () => {
      queryClient.invalidateQueries(['documents']);
      toast.success('Document archived');
      if (drawerDoc) setDrawerDoc(null);
    },
  });

  const restoreMutation = useMutation({
    mutationFn: (id) => api.post(`/documents/${id}/restore`),
    onSuccess: () => {
      queryClient.invalidateQueries(['documents']);
      toast.success('Document restored');
    },
  });

  const requestMutation = useMutation({
    mutationFn: (body) => api.post('/documents/request', body),
    onSuccess: () => {
      toast.success('Document request sent!');
      setRequestOpen(false);
      setRequestEmail('');
      setRequestName('');
      setRequestMessage('');
      setRequestFiles(['']);
    },
    onError: () => toast.error('Failed to send request'),
  });

  // --- Handlers ---
  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortBy !== field) return <ArrowUpDown className="h-3 w-3 ml-1 opacity-30" />;
    return sortOrder === 'desc'
      ? <ChevronDown className="h-3 w-3 ml-1" />
      : <ChevronUp className="h-3 w-3 ml-1" />;
  };

  const toggleSelectAll = () => {
    if (selectedDocs.length === documents.length) {
      setSelectedDocs([]);
    } else {
      setSelectedDocs(documents.map(d => d._id));
    }
  };

  const toggleSelect = (id) => {
    setSelectedDocs(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handleDownload = async (doc) => {
    try {
      const url = await api.getDocumentPresignedUrl(doc._id);
      window.open(url, '_blank');
    } catch {
      toast.error('Download failed');
    }
  };

  const handlePreview = async (doc) => {
    
    setDrawerDoc(doc);
    setDrawerTab('details');
    setPreviewLoading(true);
    try {
      const url = await api.getDocumentPresignedUrl(doc._id);
      setPreviewUrl(url);
    } catch {
      setPreviewUrl(null);
    }
    setPreviewLoading(false);
  };

  const handleReadAloud = async (doc) => {
    let summaryText = doc.ai_extracted_data?.summary || '';
    if (!summaryText && doc.ai_extracted_data?.raw_analysis) {
      try {
        const parsed = JSON.parse(doc.ai_extracted_data.raw_analysis.replace(/```json/g, '').replace(/```/g, '').trim());
        summaryText = parsed?.summary || '';
      } catch {}
    }
    if (!summaryText) {
      toast.info('Analyze this document first to read it aloud.');
      return;
    }
    setTtsPlaying(doc._id);
    try {
      const res = await api.post('/ai/tts', { text: summaryText.slice(0, 5000), lang: user?.preferred_language || 'en' });
      const audio = new Audio(api.getAudioUrl(res.audio_url));
      audio.onended = () => setTtsPlaying(null);
      audio.onerror = () => { toast.error('Playback failed'); setTtsPlaying(null); };
      await audio.play();
    } catch {
      toast.error('Read aloud failed');
      setTtsPlaying(null);
    }
  };

  const openShareDialog = (doc) => {
    setShareDoc(doc);
    setShareEmail('');
    setShareMessage('');
    setShareResult(null);
    setShareOpen(true);
  };

  const handleShare = () => {
    if (!shareDoc) return;
    shareMutation.mutate({ docId: shareDoc._id, email: shareEmail, message: shareMessage });
  };

  const handleRequestSubmit = () => {
    const files = requestFiles.filter(f => f.trim());
    if (!requestEmail.trim() || files.length === 0) {
      toast.error('Email and at least one document type required');
      return;
    }
    requestMutation.mutate({
      client_email: requestEmail,
      client_name: requestName || null,
      requested_files: files,
      message: requestMessage || null,
    });
  };

  const handleAddTag = (doc) => {
    if (!tagInput.trim()) return;
    const currentTags = doc.tags || [];
    if (currentTags.includes(tagInput.trim())) { setTagInput(''); return; }
    tagsMutation.mutate({ docId: doc._id, tags: [...currentTags, tagInput.trim()] });
    setTagInput('');
  };

  const handleRemoveTag = (doc, tag) => {
    const currentTags = doc.tags || [];
    tagsMutation.mutate({ docId: doc._id, tags: currentTags.filter(t => t !== tag) });
  };

  const handleVersionUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file || !drawerDoc) return;
    if (file.size > 25 * 1024 * 1024) {
      toast.error('File exceeds 25MB limit');
      return;
    }
    versionMutation.mutate({ docId: drawerDoc._id, file });
  };

  const navigateToFolder = (folder) => {
    setCurrentFolderId(folder._id);
    setFolderPath(prev => [...prev, { id: folder._id, name: folder.name }]);
    setSelectedDocs([]);
  };

  const navigateToRoot = () => {
    setCurrentFolderId(null);
    setFolderPath([]);
    setSelectedDocs([]);
  };

  const navigateToBreadcrumb = (idx) => {
    if (idx < 0) {
      navigateToRoot();
    } else {
      const crumb = folderPath[idx];
      setCurrentFolderId(crumb.id);
      setFolderPath(prev => prev.slice(0, idx + 1));
      setSelectedDocs([]);
    }
  };

  const addRequestFileRow = () => setRequestFiles(prev => [...prev, '']);
  const updateRequestFile = (idx, val) => setRequestFiles(prev => prev.map((f, i) => i === idx ? val : f));
  const removeRequestFileRow = (idx) => setRequestFiles(prev => prev.filter((_, i) => i !== idx));

  // ==================== RENDER ====================
  return (
    <div className="flex h-[calc(100vh-64px)]">
      {/* Main Content */}
      <div className={`flex-1 flex flex-col min-w-0 transition-all ${drawerDoc ? 'mr-[420px]' : ''}`}>
        {/* Header */}
        <div className="px-6 pt-6 pb-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold tracking-tight">{t("documents.title") || "Documents"}</h1>
              <p className="text-sm text-muted-foreground mt-0.5">
                {documents.length} document{documents.length !== 1 ? 's' : ''}
                {viewMode === 'archived' && ' ' + (t ? (t('documents.inArchive') || 'in archive') : 'in archive')}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant={viewMode === 'archived' ? 'default' : 'outline'}
                size="sm"
                onClick={() => { setViewMode(prev => prev === 'archived' ? 'all' : 'archived'); setSelectedDocs([]); }}
                className="gap-1.5"
              >
                <Archive className="h-3.5 w-3.5" />
                {viewMode === 'archived' ? (t('documents.viewAll') || 'View All') : (t('documents.archived') || 'Archived')}
              </Button>
              <Button variant="outline" onClick={() => setRequestOpen(true)} className="gap-2">
                <Send className="h-4 w-4" />
                {t("documents.requestDocs") || "Request Docs"}
              </Button>
              <Button variant="outline" onClick={() => navigate('/documents/create')} className="gap-2">
                <Plus className="h-4 w-4" />
                {t("documents.create") || "Create"}
              </Button>
              <Button onClick={() => setUploadOpen(true)} className="gap-2">
                <Upload className="h-4 w-4" />
                {t("documents.upload") || "Upload"}
              </Button>
            </div>
          </div>

          {/* Breadcrumbs */}
          {viewMode !== 'archived' && folderPath.length > 0 && (
            <div className="flex items-center gap-1 mb-3 text-sm">
              <button onClick={navigateToRoot} className="text-muted-foreground hover:text-foreground">
                {t("documents.allDocuments") || "All Documents"}
              </button>
              {folderPath.map((crumb, idx) => (
                <React.Fragment key={crumb.id}>
                  <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                  <button
                    onClick={() => navigateToBreadcrumb(idx)}
                    className={idx === folderPath.length - 1 ? 'font-medium' : 'text-muted-foreground hover:text-foreground'}
                  >
                    {crumb.name}
                  </button>
                </React.Fragment>
              ))}
            </div>
          )}

          {/* Filter Toolbar */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={t("documents.searchPlaceholder") || "Search documents..."}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 bg-card"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[140px] bg-card">
                <SelectValue placeholder={t("documents.status") || "Status"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {Object.entries(STATUS_MAP).map(([key, val]) => (
                  <SelectItem key={key} value={key}>{val.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[140px] bg-card">
                <SelectValue placeholder={t("documents.type") || "Type"} />
              </SelectTrigger>
              <SelectContent>
                {DOC_TYPES.map(t => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Folder Cards */}
        {viewMode !== 'archived' && (folders.length > 0 || currentFolderId) && (
          <div className="px-6 pb-3">
            <div className="flex items-center gap-2 flex-wrap">
              {folders.map(folder => (
                <button
                  key={folder._id}
                  onClick={() => navigateToFolder(folder)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg border bg-card hover:bg-muted/50 transition-colors group"
                >
                  <FolderOpen className="h-4 w-4 text-amber-500" />
                  <span className="text-sm font-medium">{folder.name}</span>
                  <span className="text-xs text-muted-foreground">
                    {folder.document_count || 0}
                    {folder.subfolder_count > 0 && ` · ${folder.subfolder_count} folders`}
                  </span>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                      <span className="opacity-0 group-hover:opacity-100 transition-opacity">
                        <MoreHorizontal className="h-3.5 w-3.5 text-muted-foreground" />
                      </span>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start">
                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setRenameFolderOpen(folder); setRenameFolderName(folder.name); }}>
                        <Pencil className="h-4 w-4 mr-2" /> Rename
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="text-destructive"
                        onClick={(e) => { e.stopPropagation(); deleteFolderMutation.mutate(folder._id); }}
                      >
                        <Trash2 className="h-4 w-4 mr-2" /> Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </button>
              ))}
              <button
                onClick={() => setCreateFolderOpen(true)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-dashed hover:bg-muted/50 transition-colors text-muted-foreground hover:text-foreground"
              >
                <FolderPlus className="h-4 w-4" />
                <span className="text-sm">{t("documents.newFolder") || "New Folder"}</span>
              </button>
            </div>
          </div>
        )}

        {/* New Folder button when no folders exist yet */}
        {viewMode !== 'archived' && folders.length === 0 && !currentFolderId && (
          <div className="px-6 pb-3">
            <button
              onClick={() => setCreateFolderOpen(true)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-dashed hover:bg-muted/50 transition-colors text-muted-foreground hover:text-foreground"
            >
              <FolderPlus className="h-4 w-4" />
              <span className="text-sm">{t("documents.newFolder") || "New Folder"}</span>
            </button>
          </div>
        )}

        {/* Bulk Actions Bar */}
        {selectedDocs.length > 0 && (
          <div className="mx-6 mb-2 px-4 py-2 bg-primary/5 border border-primary/20 rounded-lg flex items-center gap-3">
            <span className="text-sm font-medium">{selectedDocs.length} selected</span>
            <Button size="sm" variant="outline" onClick={() => {
              selectedDocs.forEach(id => {
                const doc = documents.find(d => d._id === id);
                if (doc) handleDownload(doc);
              });
            }}>
              <Download className="h-3.5 w-3.5 mr-1" /> Download
            </Button>
            <Button size="sm" variant="outline" onClick={() => {
              selectedDocs.forEach(id => {
                const doc = documents.find(d => d._id === id);
                if (doc && doc.status === 'uploaded') analyzeMutation.mutate(id);
              });
            }}>
              <Sparkles className="h-3.5 w-3.5 mr-1" /> Analyze
            </Button>
            <Button size="sm" variant="outline" onClick={() => setMoveDocsOpen(true)}>
              <FolderOpen className="h-3.5 w-3.5 mr-1" /> Move
            </Button>
            <Button size="sm" variant="destructive" onClick={() => bulkDeleteMutation.mutate(selectedDocs)}>
              <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setSelectedDocs([])}>{t("documents.clear") || "Clear"}</Button>
          </div>
        )}

        {/* Table */}
        <div className="flex-1 overflow-auto px-6 pb-6">
          {isLoading ? (
            <div className="space-y-3 pt-2">
              {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="h-14 w-full rounded-lg" />)}
            </div>
          ) : documents.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                <Files className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold mb-1">{t("documents.noDocumentsYet") || "No documents yet"}</h3>
              <p className="text-sm text-muted-foreground mb-4 max-w-sm">
                Upload your first document to get started with AI-powered analysis, sharing, and management.
              </p>
              <div className="flex gap-2">
                <Button onClick={() => setUploadOpen(true)}>
                  <Upload className="h-4 w-4 mr-2" /> Upload Document
                </Button>
                <Button variant="outline" onClick={() => setRequestOpen(true)}>
                  <Send className="h-4 w-4 mr-2" /> Request from Client
                </Button>
              </div>
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden bg-card">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-muted/40">
                    <th className="w-10 p-3">
                      <input
                        type="checkbox"
                        checked={selectedDocs.length === documents.length && documents.length > 0}
                        onChange={toggleSelectAll}
                        className="rounded border-muted-foreground/30"
                      />
                    </th>
                    <th className="text-left p-3">
                      <button onClick={() => toggleSort('original_filename')} className="flex items-center text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground">
                        Name <SortIcon field="original_filename" />
                      </button>
                    </th>
                    <th className="text-left p-3 w-[100px]">
                      <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("documents.status") || "Status"}</span>
                    </th>
                    <th className="text-left p-3 w-[100px] hidden lg:table-cell">
                      <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("documents.type") || "Type"}</span>
                    </th>
                    <th className="text-left p-3 w-[120px] hidden md:table-cell">
                      <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("documents.uploadedBy") || "Uploaded by"}</span>
                    </th>
                    <th className="text-left p-3 w-[90px]">
                      <button onClick={() => toggleSort('created_at')} className="flex items-center text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground">
                        Date <SortIcon field="created_at" />
                      </button>
                    </th>
                    <th className="text-left p-3 w-[80px] hidden md:table-cell">
                      <button onClick={() => toggleSort('size')} className="flex items-center text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground">
                        Size <SortIcon field="size" />
                      </button>
                    </th>
                    <th className="w-[50px] p-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {documents.map((doc) => {
                    const { icon: FileIcon, color } = getFileIcon(doc.mime_type);
                    const status = STATUS_MAP[doc.status] || STATUS_MAP.uploaded;
                    const docType = doc.ai_extracted_data?.document_type;
                    const isSelected = selectedDocs.includes(doc._id);

                    return (
                      <tr
                        key={doc._id}
                        onClick={() => handlePreview(doc)}
                        className={`border-b last:border-0 cursor-pointer transition-colors hover:bg-muted/30 ${isSelected ? 'bg-primary/5' : ''} ${drawerDoc?._id === doc._id ? 'bg-muted/40' : ''}`}
                      >
                        <td className="p-3" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleSelect(doc._id)}
                            className="rounded border-muted-foreground/30"
                          />
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-3 min-w-0">
                            <FileIcon className={`h-5 w-5 shrink-0 ${color}`} />
                            <div className="min-w-0">
                              <span className="font-medium text-sm truncate block">{doc.original_filename}</span>
                              {doc.tags?.length > 0 && (
                                <div className="flex gap-1 mt-0.5 flex-wrap">
                                  {doc.tags.slice(0, 3).map(tag => (
                                    <span key={tag} className="text-[10px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground">{tag}</span>
                                  ))}
                                  {doc.tags.length > 3 && <span className="text-[10px] text-muted-foreground">+{doc.tags.length - 3}</span>}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="p-3">
                          <Badge variant="outline" className={`text-xs ${status.color}`}>
                            {status.label}
                          </Badge>
                        </td>
                        <td className="p-3 hidden lg:table-cell">
                          {docType ? (
                            <span className="text-xs text-muted-foreground capitalize">{docType}</span>
                          ) : (
                            <span className="text-xs text-muted-foreground/50">—</span>
                          )}
                        </td>
                        <td className="p-3 hidden md:table-cell">
                          <span className="text-xs text-muted-foreground truncate block max-w-[100px]">
                            {doc.uploaded_by_name || '—'}
                          </span>
                        </td>
                        <td className="p-3">
                          <span className="text-xs text-muted-foreground whitespace-nowrap">
                            {timeAgo(doc.created_at)}
                          </span>
                        </td>
                        <td className="p-3 hidden md:table-cell">
                          <span className="text-xs text-muted-foreground">{formatBytes(doc.size)}</span>
                        </td>
                        <td className="p-3" onClick={(e) => e.stopPropagation()}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handleDownload(doc)}>
                                <Download className="h-4 w-4 mr-2" /> Download
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handlePreview(doc)}>
                                <Eye className="h-4 w-4 mr-2" /> Preview
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => openShareDialog(doc)}>
                                <Share2 className="h-4 w-4 mr-2" /> Share
                              </DropdownMenuItem>
                              {doc.status === 'uploaded' && (
                                <DropdownMenuItem onClick={() => analyzeMutation.mutate(doc._id)}>
                                  <Sparkles className="h-4 w-4 mr-2" /> Analyze with AI
                                </DropdownMenuItem>
                              )}
                              {doc.ai_extracted_data?.summary && (
                                <DropdownMenuItem onClick={() => handleReadAloud(doc)}>
                                  <Volume2 className="h-4 w-4 mr-2" /> Read Aloud
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuSeparator />
                              {doc.status === 'archived' ? (
                                <DropdownMenuItem onClick={() => restoreMutation.mutate(doc._id)}>
                                  <RotateCcw className="h-4 w-4 mr-2" /> Restore
                                </DropdownMenuItem>
                              ) : (
                                <DropdownMenuItem onClick={() => archiveMutation.mutate(doc._id)}>
                                  <Archive className="h-4 w-4 mr-2" /> Archive
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem
                                className="text-destructive focus:text-destructive"
                                onClick={() => { setDocToDelete(doc._id); setDeleteDialogOpen(true); }}
                              >
                                <Trash2 className="h-4 w-4 mr-2" /> Delete Forever
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ==================== DETAIL DRAWER ==================== */}
      {drawerDoc && (
        <div className="fixed right-0 top-[64px] bottom-0 w-[420px] bg-background border-l shadow-xl z-30 flex flex-col overflow-hidden animate-in slide-in-from-right duration-200">
          {/* Drawer Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b bg-muted/20">
            <div className="min-w-0 flex-1 mr-3">
              <div className="flex items-center gap-2">
                {(() => { const { icon: FI, color } = getFileIcon(drawerDoc.mime_type); return <FI className={`h-5 w-5 shrink-0 ${color}`} />; })()}
                <h3 className="font-semibold text-sm truncate">{drawerDoc.original_filename}</h3>
              </div>
            </div>
            <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => { setDrawerDoc(null); setPreviewUrl(null); }}>
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Drawer Tabs */}
          <div className="flex border-b bg-muted/10">
            {[
              { id: 'details', label: 'Details', icon: Eye },
              { id: 'versions', label: 'Versions', icon: History },
              { id: 'activity', label: 'Activity', icon: Activity },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setDrawerTab(tab.id)}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium transition-colors border-b-2 ${
                  drawerTab === tab.id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                <tab.icon className="h-3.5 w-3.5" />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Drawer Body */}
          <div className="flex-1 overflow-y-auto">
            {drawerTab === 'details' && <>
            {/* Preview */}
            <div className="p-5 border-b">
              {previewLoading ? (
                <div className="h-48 rounded-lg bg-muted animate-pulse flex items-center justify-center">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : previewUrl && drawerDoc.mime_type?.startsWith('image/') ? (
                <img src={previewUrl} alt={drawerDoc.original_filename} className="w-full rounded-lg border max-h-64 object-contain bg-muted/30" />
              ) : previewUrl && drawerDoc.mime_type === 'application/pdf' ? (
                <iframe src={previewUrl} title="PDF Preview" className="w-full h-64 rounded-lg border" />
              ) : (
                <div className="h-32 rounded-lg bg-muted/30 border-2 border-dashed flex flex-col items-center justify-center">
                  <File className="h-10 w-10 text-muted-foreground/50 mb-2" />
                  <p className="text-xs text-muted-foreground">{t("documents.previewNotAvailable") || "Preview not available"}</p>
                </div>
              )}
            </div>

            <div className="px-5 pb-3">
                <Button variant="outline" size="sm" className="w-full gap-2" onClick={() => navigate(`/documents/${drawerDoc._id}`)}>
                  <Maximize2 className="h-4 w-4" /> View Full Page
                </Button>
              </div>

            {/* AI Analysis */}
            {drawerDoc.ai_extracted_data ? (
              <div className="p-5 border-b">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="h-4 w-4 text-amber-500" />
                  <h4 className="text-sm font-semibold">{t("documents.aiAnalysis") || "AI Analysis"}</h4>
                </div>
                {drawerDoc.ai_extracted_data.document_type && (
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-muted-foreground">Document Type</span>
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm font-medium capitalize">{drawerDoc.ai_extracted_data.document_type}</span>
                      {drawerDoc.ai_extracted_data.confidence && (
                        <span className="text-xs text-muted-foreground">({Math.round(drawerDoc.ai_extracted_data.confidence * 100)}%)</span>
                      )}
                    </div>
                  </div>
                )}
                {drawerDoc.ai_extracted_data.summary && (
                  <div className="mb-3">
                    <span className="text-xs text-muted-foreground block mb-1">Summary</span>
                    <p className="text-sm text-foreground bg-muted/30 rounded-lg p-3 border">{drawerDoc.ai_extracted_data.summary}</p>
                  </div>
                )}
                {drawerDoc.ai_extracted_data.extracted_fields && (
                  <div>
                    <span className="text-xs text-muted-foreground block mb-1.5">Extracted Fields</span>
                    <div className="space-y-1.5">
                      {Object.entries(drawerDoc.ai_extracted_data.extracted_fields).map(([key, value]) => (
                        <div key={key} className="flex justify-between text-xs bg-muted/20 rounded px-2.5 py-1.5">
                          <span className="text-muted-foreground capitalize">{key.replace(/_/g, ' ')}</span>
                          <span className="font-medium text-foreground truncate ml-3 max-w-[180px]">{String(value)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {drawerDoc.ai_extracted_data.issues?.length > 0 && (
                  <div className="mt-3">
                    <span className="text-xs text-muted-foreground block mb-1">Issues</span>
                    {drawerDoc.ai_extracted_data.issues.map((issue, i) => (
                      <p key={i} className="text-xs text-amber-600 dark:text-amber-400 bg-amber-500/10 rounded px-2.5 py-1.5 mb-1">{issue}</p>
                    ))}
                  </div>
                )}
              </div>
            ) : drawerDoc.status === 'uploaded' ? (
              <div className="p-5 border-b">
                <div className="flex flex-col items-center py-4">
                  <Sparkles className="h-8 w-8 text-muted-foreground/40 mb-2" />
                  <p className="text-sm text-muted-foreground mb-3">Not yet analyzed</p>
                  <Button size="sm" onClick={() => analyzeMutation.mutate(drawerDoc._id)} disabled={analyzeMutation.isPending}>
                    {analyzeMutation.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
                    Analyze with AI
                  </Button>
                </div>
              </div>
            ) : null}

            {/* Metadata */}
            <div className="p-5 border-b">
              <h4 className="text-sm font-semibold mb-3">Details</h4>
              <div className="space-y-2 text-xs">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Status</span>
                  <Badge variant="outline" className={`text-xs ${(STATUS_MAP[drawerDoc.status] || STATUS_MAP.uploaded).color}`}>
                    {(STATUS_MAP[drawerDoc.status] || STATUS_MAP.uploaded).label}
                  </Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Size</span>
                  <span className="font-medium">{formatBytes(drawerDoc.size)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Uploaded by</span>
                  <span className="font-medium">{drawerDoc.uploaded_by_name || '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Date</span>
                  <span className="font-medium">{drawerDoc.created_at ? new Date(drawerDoc.created_at).toLocaleDateString() : '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">MIME Type</span>
                  <span className="font-medium">{drawerDoc.mime_type || '—'}</span>
                </div>
              </div>
            </div>

            {/* Tags */}
            <div className="p-5 border-b">
              <h4 className="text-sm font-semibold mb-2 flex items-center gap-1.5">
                <Tag className="h-3.5 w-3.5" /> Tags
              </h4>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {(drawerDoc.tags || []).map(tag => (
                  <span key={tag} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-muted text-foreground border">
                    {tag}
                    <button onClick={() => handleRemoveTag(drawerDoc, tag)} className="hover:text-destructive">
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
                {(!drawerDoc.tags || drawerDoc.tags.length === 0) && (
                  <span className="text-xs text-muted-foreground">No tags</span>
                )}
              </div>
              <div className="flex gap-1.5">
                <Input
                  placeholder={t("documents.addTag") || "Add tag..."}
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddTag(drawerDoc)}
                  className="h-8 text-xs"
                />
                <Button size="sm" variant="outline" className="h-8 px-2" onClick={() => handleAddTag(drawerDoc)}>
                  <Plus className="h-3.5 w-3.5" />
                </Button>
              </div>
              {orgTags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {orgTags.filter(t => !(drawerDoc.tags || []).includes(t.tag)).slice(0, 8).map(t => (
                    <button
                      key={t.tag}
                      onClick={() => tagsMutation.mutate({ docId: drawerDoc._id, tags: [...(drawerDoc.tags || []), t.tag] })}
                      className="text-[10px] px-1.5 py-0.5 rounded-full bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                    >
                      + {t.tag}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Status Changer */}
            <div className="p-5">
              <h4 className="text-sm font-semibold mb-2">Change Status</h4>
              <Select value={drawerDoc.status} onValueChange={(val) => statusMutation.mutate({ docId: drawerDoc._id, status: val })}>
                <SelectTrigger className="h-9 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(STATUS_MAP).map(([key, val]) => (
                    <SelectItem key={key} value={key}>
                      <span className="flex items-center gap-2">{val.label}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            </>}

            {/* VERSIONS TAB */}
            {drawerTab === 'versions' && (
              <div className="p-5">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-sm font-semibold">Version History</h4>
                  <div>
                    <input
                      type="file"
                      id="version-upload"
                      className="hidden"
                      onChange={handleVersionUpload}
                    />
                    <Button size="sm" variant="outline" className="gap-1.5" onClick={() => document.getElementById('version-upload').click()} disabled={versionMutation.isPending}>
                      {versionMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                      New Version
                    </Button>
                  </div>
                </div>

                {/* Current Version */}
                <div className="mb-3 p-3 rounded-lg border bg-primary/5 border-primary/20">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-xs font-semibold text-primary">Current (v{drawerDoc.version || 1})</span>
                      <p className="text-xs text-muted-foreground mt-0.5 truncate">{drawerDoc.original_filename}</p>
                    </div>
                    <Badge variant="outline" className="text-[10px]">{formatBytes(drawerDoc.size)}</Badge>
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-1">
                    {drawerDoc.uploaded_by_name || 'Unknown'} • {timeAgo(drawerDoc.updated_at || drawerDoc.created_at)}
                  </p>
                </div>

                {/* Previous Versions */}
                {versions.length === 0 ? (
                  <p className="text-xs text-muted-foreground text-center py-4">No previous versions</p>
                ) : (
                  <div className="space-y-2">
                    {versions.map(v => (
                      <div key={v._id} className="p-3 rounded-lg border bg-card hover:bg-muted/30 transition-colors">
                        <div className="flex items-center justify-between">
                          <div className="min-w-0 flex-1">
                            <span className="text-xs font-medium">v{v.version}</span>
                            <p className="text-xs text-muted-foreground truncate">{v.original_filename}</p>
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 px-2"
                            onClick={async () => {
                              try {
                                const { url } = await api.get(`/documents/${drawerDoc._id}/versions/${v.version}/download`);
                                window.open(url, '_blank');
                              } catch { toast.error('Download failed'); }
                            }}
                          >
                            <Download className="h-3 w-3" />
                          </Button>
                        </div>
                        <p className="text-[10px] text-muted-foreground mt-1">
                          {v.uploaded_by_name || 'Unknown'} • {timeAgo(v.created_at)}
                          {v.size && ` • ${formatBytes(v.size)}`}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ACTIVITY TAB */}
            {drawerTab === 'activity' && (
              <div className="p-5">
                <h4 className="text-sm font-semibold mb-4">Activity Log</h4>
                {activities.length === 0 ? (
                  <p className="text-xs text-muted-foreground text-center py-4">No activity recorded yet</p>
                ) : (
                  <div className="relative">
                    {/* Timeline line */}
                    <div className="absolute left-3 top-2 bottom-2 w-px bg-border" />
                    <div className="space-y-4">
                      {activities.map((a, idx) => (
                        <div key={a._id || idx} className="flex gap-3 relative">
                          <div className="w-6 h-6 rounded-full bg-muted border-2 border-background flex items-center justify-center shrink-0 z-10">
                            {a.action === 'shared' && <Share2 className="h-3 w-3 text-blue-500" />}
                            {a.action === 'analyzed' && <Sparkles className="h-3 w-3 text-amber-500" />}
                            {a.action === 'version_uploaded' && <Upload className="h-3 w-3 text-green-500" />}
                            {a.action === 'status_changed' && <RefreshCw className="h-3 w-3 text-purple-500" />}
                            {!['shared', 'analyzed', 'version_uploaded', 'status_changed'].includes(a.action) && <Activity className="h-3 w-3 text-muted-foreground" />}
                          </div>
                          <div className="flex-1 min-w-0 pb-1">
                            <p className="text-xs font-medium">{a.details || a.action}</p>
                            <p className="text-[10px] text-muted-foreground">
                              {a.user_name || 'System'} • {timeAgo(a.timestamp)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Drawer Actions */}
          <div className="p-4 border-t bg-muted/10 flex items-center gap-2 flex-wrap">
            <Button size="sm" variant="outline" onClick={() => handleDownload(drawerDoc)} className="gap-1.5">
              <Download className="h-3.5 w-3.5" /> Download
            </Button>
            <Button size="sm" variant="outline" onClick={() => openShareDialog(drawerDoc)} className="gap-1.5">
              <Share2 className="h-3.5 w-3.5" /> Share
            </Button>
            <Button size="sm" variant="outline" className="gap-1.5" onClick={() => { setDrawerTab('versions'); document.getElementById('version-upload')?.click(); }}>
              <History className="h-3.5 w-3.5" /> New Version
            </Button>
            {drawerDoc.ai_extracted_data?.summary && (
              <Button size="sm" variant="outline" onClick={() => handleReadAloud(drawerDoc)} disabled={ttsPlaying === drawerDoc._id} className="gap-1.5">
                {ttsPlaying === drawerDoc._id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Volume2 className="h-3.5 w-3.5" />}
                Read
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              className="text-destructive hover:bg-destructive/10 ml-auto gap-1.5"
              onClick={() => { setDocToDelete(drawerDoc._id); setDeleteDialogOpen(true); }}
            >
              <Trash2 className="h-3.5 w-3.5" /> Delete
            </Button>
          </div>
        </div>
      )}

      {/* ==================== UPLOAD DIALOG ==================== */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t("documents.uploadDocuments") || "Upload Documents"}</DialogTitle>
            <DialogDescription>{t("documents.uploadDesc") || "Drag and drop files or click to browse. PDF, images, Word documents supported."}</DialogDescription>
          </DialogHeader>
          <FileUploader onUploadComplete={() => { queryClient.invalidateQueries(['documents']); setUploadOpen(false); }} />
        </DialogContent>
      </Dialog>

      {/* ==================== SHARE DIALOG ==================== */}
      <Dialog open={shareOpen} onOpenChange={(open) => { setShareOpen(open); if (!open) setShareResult(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("documents.shareDocument") || "Share Document"}</DialogTitle>
            <DialogDescription>
              {shareDoc?.original_filename}
            </DialogDescription>
          </DialogHeader>

          {shareResult ? (
            <div className="space-y-4">
              <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
                <Link className="h-4 w-4 text-muted-foreground shrink-0" />
                <input
                  readOnly
                  value={shareResult.share_url}
                  className="flex-1 bg-transparent text-sm border-0 outline-none"
                  onClick={(e) => e.target.select()}
                />
                <Button size="sm" variant="ghost" onClick={() => {
                  navigator.clipboard.writeText(shareResult.share_url);
                  toast.success('Link copied!');
                }}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              {shareEmail && (
                <p className="text-sm text-muted-foreground flex items-center gap-2">
                  <Mail className="h-4 w-4" /> Sent to {shareEmail}
                </p>
              )}
              <p className="text-xs text-muted-foreground">Expires in {shareResult.expires_hours} hours</p>
              <DialogFooter>
                <Button variant="outline" onClick={() => { setShareOpen(false); setShareResult(null); }}>Done</Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="share-email">{t("documents.emailOptional") || "Email (optional)"}</Label>
                <Input
                  id="share-email"
                  type="email"
                  placeholder={t("documents.emailPlaceholder") || "client@example.com"}
                  value={shareEmail}
                  onChange={(e) => setShareEmail(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">Leave empty to just generate a link</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="share-msg">{t("documents.messageOptional") || "Message (optional)"}</Label>
                <Textarea
                  id="share-msg"
                  placeholder={t("documents.shareMsgPlaceholder") || "Here's the document you requested..."}
                  value={shareMessage}
                  onChange={(e) => setShareMessage(e.target.value)}
                  className="min-h-[80px]"
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShareOpen(false)}>{t("common.cancel") || "Cancel"}</Button>
                <Button onClick={handleShare} disabled={shareMutation.isPending} className="gap-2">
                  {shareMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Share2 className="h-4 w-4" />}
                  {shareEmail ? 'Share & Send' : 'Create Link'}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ==================== REQUEST DOCS DIALOG ==================== */}
      <Dialog open={requestOpen} onOpenChange={setRequestOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("documents.requestDocuments") || "Request Documents"}</DialogTitle>
            <DialogDescription>{t("documents.requestDesc") || "Send a secure upload link to a client via email."}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>{t("documents.clientEmail") || "Client Email *"}</Label>
                <Input
                  type="email"
                  placeholder="client@example.com"
                  value={requestEmail}
                  onChange={(e) => setRequestEmail(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>{t("documents.clientName") || "Client Name"}</Label>
                <Input
                  placeholder={t("documents.namePlaceholder") || "John Doe"}
                  value={requestName}
                  onChange={(e) => setRequestName(e.target.value)}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t("documents.requestedDocuments") || "Requested Documents *"}</Label>
              {requestFiles.map((file, idx) => (
                <div key={idx} className="flex gap-2">
                  <Input
                    placeholder={idx === 0 ? "e.g. Driver's License" : "e.g. Tax Return"}
                    value={file}
                    onChange={(e) => updateRequestFile(idx, e.target.value)}
                  />
                  {requestFiles.length > 1 && (
                    <Button variant="ghost" size="icon" className="shrink-0 h-10 w-10" onClick={() => removeRequestFileRow(idx)}>
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
              <Button variant="link" size="sm" className="px-0 h-auto" onClick={addRequestFileRow}>
                + Add another document
              </Button>
            </div>
            <div className="space-y-2">
              <Label>{t("documents.messageOptional") || "Message (optional)"}</Label>
              <Textarea
                placeholder={t("documents.requestMsgPlaceholder") || "Please upload the following documents..."}
                value={requestMessage}
                onChange={(e) => setRequestMessage(e.target.value)}
                className="min-h-[80px]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRequestOpen(false)}>Cancel</Button>
            <Button onClick={handleRequestSubmit} disabled={requestMutation.isPending} className="gap-2">
              {requestMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Send Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ==================== CREATE FOLDER DIALOG ==================== */}
      <Dialog open={createFolderOpen} onOpenChange={setCreateFolderOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>New Folder</DialogTitle>
          </DialogHeader>
          <Input
            placeholder={t("documents.folderName") || "Folder name"}
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && newFolderName.trim()) {
                createFolderMutation.mutate({ name: newFolderName.trim(), parent_id: currentFolderId || null });
              }
            }}
            autoFocus
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateFolderOpen(false)}>Cancel</Button>
            <Button
              onClick={() => createFolderMutation.mutate({ name: newFolderName.trim(), parent_id: currentFolderId || null })}
              disabled={!newFolderName.trim() || createFolderMutation.isPending}
            >
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ==================== RENAME FOLDER DIALOG ==================== */}
      <Dialog open={!!renameFolderOpen} onOpenChange={(open) => !open && setRenameFolderOpen(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{t("documents.renameFolder") || "Rename Folder"}</DialogTitle>
          </DialogHeader>
          <Input
            placeholder="Folder name"
            value={renameFolderName}
            onChange={(e) => setRenameFolderName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && renameFolderName.trim() && renameFolderOpen) {
                renameFolderMutation.mutate({ folderId: renameFolderOpen._id, name: renameFolderName.trim() });
              }
            }}
            autoFocus
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameFolderOpen(null)}>Cancel</Button>
            <Button
              onClick={() => renameFolderOpen && renameFolderMutation.mutate({ folderId: renameFolderOpen._id, name: renameFolderName.trim() })}
              disabled={!renameFolderName.trim()}
            >
              Rename
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ==================== MOVE TO FOLDER DIALOG ==================== */}
      <Dialog open={moveDocsOpen} onOpenChange={setMoveDocsOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Move {selectedDocs.length} document{selectedDocs.length !== 1 ? 's' : ''}</DialogTitle>
            <DialogDescription>{t("documents.selectDestination") || "Select a destination folder."}</DialogDescription>
          </DialogHeader>
          <div className="space-y-1 max-h-64 overflow-y-auto">
            <button
              onClick={() => moveDocsMutation.mutate({ document_ids: selectedDocs, folder_id: null })}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-muted text-left text-sm"
            >
              <Files className="h-4 w-4 text-muted-foreground" />
              Root (no folder)
            </button>
            {folders.map(f => (
              <button
                key={f._id}
                onClick={() => moveDocsMutation.mutate({ document_ids: selectedDocs, folder_id: f._id })}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-muted text-left text-sm"
              >
                <FolderOpen className="h-4 w-4 text-amber-500" />
                {f.name}
                <span className="text-xs text-muted-foreground ml-auto">{f.document_count || 0} docs</span>
              </button>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMoveDocsOpen(false)}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ==================== DELETE DIALOG ==================== */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("documents.deleteDocument") || "Delete document?"}</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the document and remove it from storage. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => docToDelete && deleteMutation.mutate(docToDelete)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
